/**
 * Push Notification Manager
 * Handles browser push notification subscriptions using the Web Push API
 */

const VAPID_PUBLIC_KEY_URL = `${process.env.REACT_APP_BACKEND_URL}/api/push-notifications/vapid-public-key`;
const SUBSCRIBE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/push-notifications/subscribe`;
const UNSUBSCRIBE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/push-notifications/unsubscribe`;
const STATUS_URL = `${process.env.REACT_APP_BACKEND_URL}/api/push-notifications/status`;

/**
 * Check if push notifications are supported in this browser
 */
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

/**
 * Get current notification permission status
 * @returns 'granted' | 'denied' | 'default'
 */
export const getPermissionStatus = () => {
  if (!('Notification' in window)) return 'denied';
  return Notification.permission;
};

/**
 * Request notification permission from the user
 * @returns Promise<'granted' | 'denied' | 'default'>
 */
export const requestPermission = async () => {
  if (!isPushSupported()) {
    console.warn('Push notifications not supported');
    return 'denied';
  }
  
  const permission = await Notification.requestPermission();
  return permission;
};

/**
 * Safely parse a fetch Response body even when an upstream interceptor
 * (e.g. Emergent platform telemetry) has already consumed it on non-2xx
 * responses. Returns {} if the body is unreadable.
 *
 * Background: the Emergent platform's telemetry layer wraps window.fetch
 * and calls `response.text()` on the original Response (not a clone) for
 * any non-OK status. Our subsequent `await response.json()` then throws
 * "Failed to execute 'json' on 'Response': body stream already read".
 */
const _safeJson = async (response) => {
  try {
    // Cloning before reading is the safe path when the body is still intact
    return await response.clone().json();
  } catch (_) {
    try { return await response.json(); } catch (_) { return {}; }
  }
};
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

/**
 * Subscribe to push notifications
 * @param {string} token - Auth token for API calls
 * @returns Promise<{success: boolean, message: string}>
 */
export const subscribeToPush = async (token) => {
  try {
    // Check support
    if (!isPushSupported()) {
      return { success: false, message: 'Push notifications not supported in this browser' };
    }

    // Request permission
    const permission = await requestPermission();
    if (permission !== 'granted') {
      return { success: false, message: 'Notification permission denied' };
    }

    // Register service worker
    const registration = await navigator.serviceWorker.ready;

    // Get VAPID public key from backend
    const vapidResponse = await fetch(VAPID_PUBLIC_KEY_URL);
    if (!vapidResponse.ok) {
      return { success: false, message: `VAPID key fetch failed (${vapidResponse.status})` };
    }
    const vapidData = await _safeJson(vapidResponse);
    const public_key = vapidData.public_key;
    if (!public_key) {
      return { success: false, message: 'VAPID public key missing from server response' };
    }

    // Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key)
    });

    // Send subscription to backend
    const response = await fetch(SUBSCRIBE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))))
        },
        user_agent: navigator.userAgent
      })
    });

    if (response.ok) {
      return { success: true, message: 'Successfully subscribed to push notifications' };
    }

    // Error path — body may already be drained by an upstream interceptor,
    // so prefer status/statusText and only attempt JSON as a best-effort.
    const result = await _safeJson(response);
    return {
      success: false,
      message: result.detail || result.message || `Subscribe failed (${response.status} ${response.statusText})`,
    };

  } catch (error) {
    console.error('Push subscription error:', error);
    return { success: false, message: error.message || 'Failed to subscribe' };
  }
};

/**
 * Unsubscribe from push notifications
 * @param {string} token - Auth token for API calls
 * @returns Promise<{success: boolean, message: string}>
 */
export const unsubscribeFromPush = async (token) => {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      return { success: true, message: 'No subscription found' };
    }

    // Unsubscribe from browser
    await subscription.unsubscribe();

    // Notify backend
    const response = await fetch(UNSUBSCRIBE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        keys: {}
      })
    });

    if (response.ok) {
      return { success: true, message: 'Successfully unsubscribed' };
    } else {
      return { success: false, message: 'Failed to unsubscribe from server' };
    }

  } catch (error) {
    console.error('Push unsubscribe error:', error);
    return { success: false, message: error.message || 'Failed to unsubscribe' };
  }
};

/**
 * Check current push subscription status
 * @param {string} token - Auth token for API calls
 * @returns Promise<{isSubscribed: boolean, subscriptionCount: number}>
 */
export const getPushStatus = async (token) => {
  try {
    // Check local subscription first
    if (!isPushSupported()) {
      return { isSubscribed: false, subscriptionCount: 0 };
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      return { isSubscribed: false, subscriptionCount: 0 };
    }

    // Verify with backend
    const response = await fetch(STATUS_URL, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.ok) {
      const data = await _safeJson(response);
      return {
        isSubscribed: !!data.is_subscribed,
        subscriptionCount: data.subscription_count || 0
      };
    }

    return { isSubscribed: !!subscription, subscriptionCount: subscription ? 1 : 0 };

  } catch (error) {
    console.error('Push status check error:', error);
    return { isSubscribed: false, subscriptionCount: 0 };
  }
};

/**
 * Show a local notification (for testing)
 * @param {string} title 
 * @param {object} options 
 */
export const showLocalNotification = (title, options = {}) => {
  if (!isPushSupported() || Notification.permission !== 'granted') {
    console.warn('Cannot show notification - not supported or not permitted');
    return;
  }

  const defaultOptions = {
    icon: '/logo192.png',
    badge: '/badge-72x72.png',
    vibrate: [200, 100, 200],
    ...options
  };

  new Notification(title, defaultOptions);
};

export default {
  isPushSupported,
  getPermissionStatus,
  requestPermission,
  subscribeToPush,
  unsubscribeFromPush,
  getPushStatus,
  showLocalNotification
};
